import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import FAQ from '@src/components/DisposalPage/FAQ'
import LandingVideo from '@src/components/DisposalPage/LandingVideo'
import HexagonFeatures from '@src/components/DisposalPage/HexagonFeatures'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import solarClients from '@src/constants/solarClients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/junk-removal-page.module.css'
// eslint-disable-next-line import/extensions
import solar from '../../json/solar-panel-sell-data.json'

const sellServiceData = {
  name: 'Sell Used and New Solar Panels & Equipment',
  description:
    'Sell used and new solar panels and equipment to Dimension. Get a competitive offer on functional panels, batteries, and inverters, with free testing, nationwide pickup, and a certificate of recycling for anything that can not be reused.',
  serviceType: 'Solar Panel Buyback and Recycling',
  url: 'https://getdimension.com/solar-panel-sell',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Buy Used Solar Panels' },
    { name: 'Buy New and Surplus Solar Panels' },
    { name: 'Solar Equipment Buyback' },
    { name: 'Panel Testing and Refurbishment' },
  ],
}

const sellFAQData = {
  questions: solar.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function SolarPanelSell() {
  return (
    <>
      <Head>
        <title>Sell Used Solar Panels &amp; Equipment | Dimension</title>
        <meta
          name="description"
          content="Sell your used and new solar panels and equipment to Dimension. Competitive offers on functional panels, free testing, nationwide pickup, and no transport fees on large orders."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/solar-panel-sell" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={sellServiceData} />
        <SchemaMarkup type="FAQPage" data={sellFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heroImage="solar"
          heading={solar.pitch.heading}
          material={solar.pitch.material}
          pitchText={solar.pitch.pitchText}
          ctaLink={solar.pitch.ctaLink}
          ctaText={solar.pitch.ctaText}
          ctaHeading={solar.pitch.ctaHeading}
          ctaSubtext={solar.pitch.ctaSubtext}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <LandingVideo service="solar-panel-sell" heading="How Selling Your Panels Works" />
        <Clients clients={solarClients} />
        <HexagonFeatures />
        <FeatureList {...solar.solutions} />
        <FeatureList {...solar.benefits} />
        <Approach {...solar.approach} />
        <FAQ {...solar.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Solar Panel Disposal & Recycling',
              href: '/solar-panel-disposal',
              description:
                'Recycle old, damaged, and decommissioned panels that are past the point of resale.',
            },
            {
              label: 'Solar Decommissioning',
              href: '/solar-decommissioning',
              description:
                'Full commercial and utility-scale project teardown, with salvage value recovered from your array.',
            },
            {
              label: 'E-Waste Management',
              href: '/ewaste',
              description:
                'Certified recycling for the batteries, inverters, and electronics that come with a solar system.',
            },
            {
              label: 'Sustainability',
              href: '/sustainability',
              description:
                'How Dimension keeps working equipment in service and reports on landfill diversion.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default SolarPanelSell
